import { useEffect, useRef } from "react";

const SCROLL_POSITION_KEY = "scheduleScrollPosition";
const SCROLL_CONTAINER_CLASS = ".scheduleHorizontalScroll";

/**
 * Persists Gantt chart horizontal scroll position to localStorage.
 *
 * Note: Uses DOM queries due to @ecerto/gantt-chart library limitations.
 * The library doesn't expose scroll position control via props/refs.
 *
 * @returns containerRef - Ref to attach to the wrapper element containing the Gantt chart
 *
 * @example
 * const ganttContainerRef = useGanttScrollPersistence();
 * return <Box ref={ganttContainerRef}><Gantt {...props} /></Box>
 */
export default function useGanttScrollPersistence() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = containerRef.current?.querySelector(
      SCROLL_CONTAINER_CLASS
    ) as HTMLElement | null;

    if (!scrollContainer) return;

    // Restore scroll position on mount
    const savedPosition = localStorage.getItem(SCROLL_POSITION_KEY);
    if (savedPosition) {
      scrollContainer.scrollLeft = parseInt(savedPosition, 10);
    }

    // Save scroll position on scroll
    const handleScroll = () => {
      localStorage.setItem(SCROLL_POSITION_KEY, scrollContainer.scrollLeft.toString());
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []); // Empty array - only run once on mount

  return containerRef;
}
